import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import NotificationBell from '../components/NotificationBell';
import { 
  ArrowRight, 
  FileText, 
  User, 
  Briefcase, 
  LogOut,
  CheckCircle,
  Clock,
  Upload,
  Settings,
  ChevronRight,
  TrendingUp,
  Sparkles,
  Zap,
  HelpCircle,
  MessageSquare,
  BookOpen,
  Check
} from 'lucide-react';

// Dashboard Help Modal Component
const DashboardHelpModal = ({ isOpen, onClose, onNavigateToHelp }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-lime-600" />
            Your Dashboard
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">What is this page?</h4>
            <p className="text-sm text-slate-600">
              This is your personal command center. From here, you can track your profile completion, view job matches, manage your resumes, and access all platform features.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">Why does it matter?</h4>
            <p className="text-sm text-slate-600">
              A complete profile (100%) significantly improves your V-FIT matching scores. The more we know about your preferences, the better we can match you with opportunities.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Tips</h4>
            <ul className="space-y-2">
              {[
                'Complete all 8 sections for the best matching results',
                'Check "Job Matches" regularly for new opportunities',
                'Keep your resume up-to-date for accurate matching',
                'Use Market Pulse to stay informed about industry trends'
              ].map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={onNavigateToHelp}
              className="text-sm text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
            >
              <BookOpen className="h-4 w-4" />
              View full Help Center
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function DashboardPage() {
  const { user, profile, preferences, onboardingState, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [showHelp, setShowHelp] = useState(false);

  const completionPercent = user?.onboarding_completion_percent || 0;
  const isOnboardingComplete = completionPercent === 100;

  // Fetch unread ticket count
  useEffect(() => {
    const fetchUnreadTickets = async () => {
      try {
        const response = await authAxios.get('/candidates/support/tickets/unread/count');
        setUnreadTickets(response.data.unread_count || 0);
      } catch (e) {
        // Ignore errors
      }
    };
    fetchUnreadTickets();
  }, [authAxios]);

  // Calculate section statuses
  const sections = [
    { key: 'section_about_you_status', name: 'About You', icon: User },
    { key: 'section_industry_function_status', name: 'Industries & Functions', icon: Briefcase },
    { key: 'section_company_preferences_status', name: 'Company Preferences', icon: Settings },
    { key: 'section_work_style_status', name: 'Work Style', icon: Clock },
    { key: 'section_compensation_status', name: 'Compensation', icon: Briefcase },
    { key: 'section_dealbreakers_status', name: 'Deal Makers & Breakers', icon: CheckCircle },
    { key: 'section_disease_areas_status', name: 'Disease Areas', icon: FileText },
    { key: 'section_resume_status', name: 'Resume', icon: Upload },
  ];

  const getSectionStatus = (key) => {
    return onboardingState?.[key] || 'NOT_STARTED';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'COMPLETE':
        return <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100">Complete</Badge>;
      case 'IN_PROGRESS':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">In Progress</Badge>;
      default:
        return <Badge variant="secondary">Not Started</Badge>;
    }
  };

  const handleStartOnboarding = () => {
    // Find first incomplete section
    const firstIncomplete = sections.findIndex(
      (s) => getSectionStatus(s.key) !== 'COMPLETE'
    );
    navigate('/onboarding', { state: { startSection: firstIncomplete >= 0 ? firstIncomplete : 0 } });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
            </Link>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-slate-500 hover:text-lime-600" 
                onClick={() => setShowHelp(true)}
                data-testid="dashboard-help-btn"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
              <NotificationBell />
              <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={logout}
                data-testid="dashboard-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid gap-8">
          {/* Welcome Section */}
          <div className="animate-slideUp">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-heading mb-2">
              {profile?.first_name ? `Welcome back, ${profile.first_name}!` : 'Welcome to Varyynt'}
            </h1>
            <p className="text-slate-600">
              {isOnboardingComplete 
                ? 'Your profile is complete. We\'ll notify you when matching opportunities arise.'
                : 'Complete your profile to get matched with opportunities that fit your goals.'}
            </p>
          </div>

          {/* Progress Card */}
          <Card className="shadow-sm border-slate-100 animate-slideUp stagger-1" data-testid="onboarding-progress-card">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Profile Completion</CardTitle>
                  <CardDescription>
                    {isOnboardingComplete 
                      ? 'Your profile is 100% complete'
                      : `${completionPercent}% complete — keep going!`}
                  </CardDescription>
                </div>
                <div className="text-3xl font-bold text-lime-600" data-testid="completion-percent">
                  {completionPercent}%
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Progress value={completionPercent} className="h-3 mb-6" />
              
              {!isOnboardingComplete && (
                <Button 
                  className="bg-evergreen-950 hover:bg-evergreen-900 text-white"
                  onClick={handleStartOnboarding}
                  data-testid="start-profile-btn"
                >
                  {completionPercent > 0 ? 'Continue Profile' : 'Start Your Profile'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Section Progress Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 animate-slideUp stagger-2">
            {sections.map((section, index) => {
              const status = getSectionStatus(section.key);
              const Icon = section.icon;
              return (
                <Card 
                  key={section.key}
                  className={`shadow-sm border-slate-100 cursor-pointer hover:shadow-md hover:border-teal-200 transition-all ${
                    status === 'COMPLETE' ? 'bg-lime-50/50' : ''
                  }`}
                  onClick={() => navigate('/onboarding', { state: { startSection: index } })}
                  data-testid={`section-card-${section.key}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                          status === 'COMPLETE' ? 'bg-teal-100' : 'bg-slate-100'
                        }`}>
                          <Icon className={`h-5 w-5 ${
                            status === 'COMPLETE' ? 'text-lime-600' : 'text-slate-500'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-slate-900">{section.name}</p>
                          {getStatusBadge(status)}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Profile Summary (if onboarding complete) */}
          {isOnboardingComplete && (
            <Card className="shadow-sm border-evergreen-100 bg-gradient-to-br from-evergreen-950 to-evergreen-900 text-white animate-slideUp stagger-3" data-testid="profile-summary-card">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div>
                    <h2 className="text-2xl font-heading mb-2">Your profile is ready</h2>
                    <p className="text-white/70 max-w-lg">
                      View your complete career preferences, see your AI-generated summary, and make quick edits anytime.
                    </p>
                  </div>
                  <Button 
                    className="bg-lime-500 hover:bg-lime-400 text-evergreen-950 font-medium shadow-lg"
                    onClick={() => navigate('/profile')}
                    data-testid="view-profile-btn"
                  >
                    View My Profile
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Job Matches Card - Prominent when onboarding complete */}
          {isOnboardingComplete && (
            <Card className="shadow-lg border-2 border-lime-300 bg-gradient-to-br from-lime-50 via-white to-emerald-50 animate-slideUp stagger-3" data-testid="job-matches-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-lime-500 to-emerald-500 flex items-center justify-center shadow-lg">
                    <Zap className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-slate-900">Your Job Matches</h3>
                      <Badge className="bg-lime-100 text-lime-700">
                        <Sparkles className="h-3 w-3 mr-1" />
                        AI-Powered
                      </Badge>
                    </div>
                    <p className="text-slate-600">
                      Your personal career agent is actively finding roles that match your skills and aspirations. 
                      We only show you strong fits — not a job board, but curated opportunities.
                    </p>
                  </div>
                  <Button 
                    className="bg-evergreen-950 hover:bg-evergreen-900 shadow-lg px-6"
                    onClick={() => navigate('/matches')}
                    data-testid="view-matches-btn"
                  >
                    View Matches
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Career Insights Card */}
          {isOnboardingComplete && (
            <Card className="shadow-md border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-indigo-50 animate-slideUp stagger-4" data-testid="career-insights-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-lg">
                    <TrendingUp className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-slate-900">Career Insights</h3>
                      <Badge className="bg-purple-100 text-purple-700">
                        New
                      </Badge>
                    </div>
                    <p className="text-slate-600">
                      Visualize your career journey and explore AI-powered pathway recommendations based on your experience.
                    </p>
                  </div>
                  <Button 
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50 shadow-sm px-5"
                    onClick={() => navigate('/career-insights')}
                    data-testid="view-career-insights-btn"
                  >
                    Explore
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Support Ticket Update Card - Only show when there are unread responses */}
          {unreadTickets > 0 && (
            <Card className="shadow-md border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-orange-50 animate-slideUp" data-testid="ticket-update-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center shadow-lg relative">
                    <MessageSquare className="h-6 w-6 text-white" />
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
                      {unreadTickets}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">Support has responded!</h3>
                    <p className="text-sm text-slate-600">
                      You have {unreadTickets} new response{unreadTickets > 1 ? 's' : ''} to your support ticket{unreadTickets > 1 ? 's' : ''}.
                    </p>
                  </div>
                  <Button 
                    className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg"
                    onClick={() => navigate('/my-tickets')}
                    data-testid="view-tickets-btn"
                  >
                    View Response{unreadTickets > 1 ? 's' : ''}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div className="grid md:grid-cols-3 gap-4 animate-slideUp stagger-4">
            <Card className="shadow-sm border-lime-200 bg-gradient-to-br from-lime-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-lime-100 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-lime-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-slate-900">Market Pulse</h3>
                    <p className="text-sm text-slate-500">Weekly industry insights</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/market-pulse')}
                    data-testid="market-pulse-btn"
                    className="border-lime-300 hover:bg-lime-50"
                  >
                    View
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-sm border-slate-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-slate-900">Resume Management</h3>
                    <p className="text-sm text-slate-500">Upload and manage your resumes</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/resumes')}
                    data-testid="manage-resumes-btn"
                  >
                    Manage
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card className="shadow-sm border-slate-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Settings className="h-6 w-6 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-slate-900">Account Settings</h3>
                    <p className="text-sm text-slate-500">Manage your account preferences</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate('/settings')}
                    data-testid="account-settings-btn"
                  >
                    Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Dashboard Help Modal */}
      <DashboardHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        onNavigateToHelp={() => {
          setShowHelp(false);
          navigate('/help');
        }}
      />
    </div>
  );
}
